import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ContractAnalysisType,
  CreateTask,
  useGetContractsQuery,
  ErrorMessage,
  getSubstanceText,
  ModalDialog,
  Mtds,
  templates,
  useCreateTaskMutation,
  useGetBatchDataQuery,
  widgets,
  useAddContractAnalysisTypeMutation,
  CheckCircleIcon,
  SinglePlan,
  useGetPurposesQuery,
  useGetSubPurposesQuery,
  toUserMessage
} from 'prove-common'
import { getTaskSchema, type TaskFormData } from '../../schemas/task-schema'
import { MatrixAmountFields } from '../matrix-amount-fields/MatrixAmountFields'
import {
  emptyMatrixAmountDraft,
  parseMatrixAmountDraft,
  type MatrixAmountDraft
} from '../../utils/matrixAmount'
import { getCreateTaskUiSchema } from '../../schemas/task-ui-schema'
import { useExistingOrCreateAnalysisType } from '../../hooks/useExistingOrCreateAnalysisType'
import {
  filterBatchData,
  findValidContractAnalysisTypes
} from '../../utils/taskUtils'
import { Form, RJSFSchema, validator } from 'prove-common/rjsf'
import { useTaskReceptionAddress } from '../../hooks/useTaskReceptionAddress'
import { TaskReceptionAddress } from '../task-reception-address/TaskReceptionAddress'

type CreateTaskModalProps = {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  plan: SinglePlan
  debug?: boolean
}

type StepHeaderProps = {
  index: number
  title: string
  canActivate: boolean
  currentStep?: number
  alertLabel?: string
  isFinished: boolean
  onToggleRequest?: () => void
}

const StepHeader = ({
  index,
  title,
  currentStep,
  canActivate,
  alertLabel,
  isFinished,
  onToggleRequest
}: StepHeaderProps) => {
  return (
    <Mtds.Details.Summary
      className={canActivate ? undefined : 'opacity-60'}
      onClick={(event) => {
        event.preventDefault()
        if (!canActivate) return
        onToggleRequest?.()
      }}
    >
      <Mtds.Grid>
        <Mtds.Flex
          data-justify={'space-between'}
          data-align={'center'}
          data-size={'lg'}
        >
          <Mtds.Flex data-align={'center'}>
            {isFinished && <CheckCircleIcon />}
            {title}
          </Mtds.Flex>
          <div
            data-icon='none'
            data-size={'md'}
            className={`rounded-md px-2 py-1 ${index === currentStep ? 'bg-surface-tinted' : undefined}`}
          >
            {index} av 3
          </div>
        </Mtds.Flex>
        {alertLabel && (
          <Mtds.Alert data-color={'info'} data-size={'md'}>
            <Mtds.Heading>{alertLabel}</Mtds.Heading>
          </Mtds.Alert>
        )}
      </Mtds.Grid>
    </Mtds.Details.Summary>
  )
}

const buildSubsetSchema = (
  fullSchema: RJSFSchema | null,
  keys: string[]
): RJSFSchema | Record<string, never> => {
  if (!fullSchema) return {}
  const { properties = {}, required = [], ...rest } = fullSchema
  const subProps = Object.fromEntries(
    Object.entries(properties).filter(([key]) => keys.includes(key))
  )
  const subRequired = required.filter((requiredKey) =>
    keys.includes(requiredKey)
  )
  return { ...rest, properties: subProps, required: subRequired }
}

const step0Keys = ['labId', 'contractId']
const step1Keys = ['productId', 'matrixId', 'substanceIds', 'payloadSchemaId']
const step2Keys = [
  'sampntTypeId',
  'ordningsverdiId',
  'subPurposeId',
  'description',
  'locationRequired'
]
const step3Keys = ['quantity', 'countSubSamples']

const defaultFormStateBehavior = {
  constAsDefaults: 'skipOneOf' as const
}

export default function CreateTaskModal(props: Readonly<CreateTaskModalProps>) {
  return props.isOpen ? <CreateTaskForm key={props.plan.id} {...props} /> : null
}

function CreateTaskForm({
  isOpen,
  setOpen,
  plan,
  debug
}: Readonly<CreateTaskModalProps>) {
  const { id: planId, contractId: parentContractId, status: planStatus } = plan
  const { data: contracts } = useGetContractsQuery(isOpen)
  const parentContract = useMemo(
    () =>
      parentContractId
        ? contracts?.find((contract) => contract.id === parentContractId)
        : undefined,
    [contracts, parentContractId]
  )
  const parentLabId = parentContract?.labId
  // steps: 0 Laboratorium (kontrakt og mottaksadresse; lukket som standard når kontrakt arves fra plan), 1 Prøvemateriale, 2 Tilleggsinformasjon, 3 Antall prøver
  const [currentStep, setCurrentStep] = useState<number | undefined>(undefined)
  const [formData, setFormData] = useState<Partial<TaskFormData> | undefined>()
  const [saveErrorMessage, setSaveErrorMessage] = useState<string>()
  const [matrixAmountDraft, setMatrixAmountDraft] = useState<MatrixAmountDraft>(
    emptyMatrixAmountDraft
  )
  const { amount: matrixAmount, error: matrixAmountError } =
    parseMatrixAmountDraft(matrixAmountDraft)
  const receptionAddress = useTaskReceptionAddress(
    formData?.labId,
    formData?.labReceptionAddressId,
    isOpen
  )
  const initializedAddressLab = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (!isOpen || formData?.labId == null) {
      initializedAddressLab.current = undefined
      return
    }
    if (
      receptionAddress.isSuccess &&
      initializedAddressLab.current !== formData.labId
    ) {
      initializedAddressLab.current = formData.labId
      const defaultId =
        receptionAddress.data.find((address) => address.isDefault)?.id ?? null
      setFormData((current) => ({
        ...current,
        labReceptionAddressId: defaultId
      }))
    }
  }, [
    isOpen,
    formData?.labId,
    receptionAddress.isSuccess,
    receptionAddress.data
  ])
  useEffect(() => {
    if (isOpen) {
      if (parentLabId != null && parentContractId != null) {
        setFormData({
          labId: parentLabId,
          contractId: parentContractId
        })
        setCurrentStep(1)
      } else {
        setCurrentStep(0)
      }
    }
  }, [parentLabId, parentContractId, isOpen])
  const addressNeedsAttention =
    receptionAddress.isError ||
    (receptionAddress.isSuccess &&
      receptionAddress.data.length > 0 &&
      !receptionAddress.data.some((address) => address.isDefault))
  const openedStep0ForAddress = useRef(false)
  useEffect(() => {
    if (!isOpen) {
      openedStep0ForAddress.current = false
      return
    }
    if (
      parentContractId != null &&
      addressNeedsAttention &&
      !openedStep0ForAddress.current
    ) {
      openedStep0ForAddress.current = true
      setCurrentStep(0)
    }
  }, [isOpen, parentContractId, addressNeedsAttention])

  const {
    data: batchData,
    isPending: isPendingBatchData,
    isError: isErrorBatchData,
    error: batchDataError
  } = useGetBatchDataQuery(isOpen)
  const { data: subPurposes } = useGetSubPurposesQuery(isOpen)
  const { data: purposes } = useGetPurposesQuery(isOpen)
  const purposeName = purposes?.find((r) => r.id === plan.purposeId)?.name
  const createTaskUiSchema = useMemo(
    () => getCreateTaskUiSchema(purposeName),
    [purposeName]
  )
  const createTask = useCreateTaskMutation()
  const existingOrCreateAnalysisType = useExistingOrCreateAnalysisType(
    batchData?.sampleTypes,
    batchData?.analysisTypes
  )
  const addContractAnalysisType = useAddContractAnalysisTypeMutation()
  const isSaving =
    existingOrCreateAnalysisType.isPending ||
    addContractAnalysisType.isPending ||
    createTask.isPending
  const hasMatrixAmount = matrixAmount != null && matrixAmountError == null
  const canProceedFromStep0 = (): boolean => formData?.contractId != null
  const canProceedFromStep1 = (): boolean =>
    formData?.productId != null &&
    formData?.matrixId != null &&
    (formData?.substanceIds?.length ?? 0) > 0 &&
    formData?.payloadSchemaId != null
  const canProceedFromStep2 = (): boolean =>
    formData?.sampntTypeId != null && formData?.ordningsverdiId != null
  const canSubmit = (): boolean =>
    receptionAddress.canSave &&
    hasMatrixAmount &&
    formData?.quantity != null &&
    formData?.countSubSamples != null &&
    canProceedFromStep1() &&
    canProceedFromStep2()
  const validAnalysisTypes = useMemo(
    () =>
      findValidContractAnalysisTypes({
        formData,
        contracts
      }),
    [contracts, formData]
  )
  const matchingAnalysisTypes = useMemo(
    () =>
      validAnalysisTypes.flatMap((contract) => contract.analysisTypes ?? []),
    [validAnalysisTypes]
  )
  const hasAnyCombination = matchingAnalysisTypes.length > 0
  const hasApprovedCombination = matchingAnalysisTypes.some(
    (analysisType) => analysisType.mtApproval && analysisType.labApproval
  )
  const showAwaitingApprovalWarning =
    canProceedFromStep1() && hasAnyCombination && !hasApprovedCombination
  const showNewCombinationWarning = canProceedFromStep1() && !hasAnyCombination
  let step1AlertLabel: string | undefined
  if (showAwaitingApprovalWarning) {
    step1AlertLabel = 'Avventer'
  } else if (showNewCombinationWarning) {
    step1AlertLabel = 'Ny kombinasjon sendes til godkjenning'
  }
  const validContracts = useMemo(
    () =>
      formData?.labId
        ? contracts?.filter((contract) => contract?.labId === formData.labId)
        : contracts,
    [contracts, formData?.labId]
  )
  const { valid, invalid } = useMemo(
    () =>
      batchData
        ? filterBatchData(batchData, validAnalysisTypes)
        : { valid: null, invalid: null },
    [batchData, validAnalysisTypes]
  )
  const fullSchema: RJSFSchema | null = useMemo(
    () =>
      valid && invalid
        ? getTaskSchema(valid, invalid, validContracts ?? [], subPurposes ?? [])
        : null,
    [invalid, subPurposes, valid, validContracts]
  )

  const step0Schema = useMemo(
    () => (currentStep === 0 ? buildSubsetSchema(fullSchema, step0Keys) : {}),
    [currentStep, fullSchema]
  )
  const step1Schema = useMemo(
    () => (currentStep === 1 ? buildSubsetSchema(fullSchema, step1Keys) : {}),
    [currentStep, fullSchema]
  )
  const step2Schema = useMemo(
    () => (currentStep === 2 ? buildSubsetSchema(fullSchema, step2Keys) : {}),
    [currentStep, fullSchema]
  )
  const step3Schema = useMemo(
    () => (currentStep === 3 ? buildSubsetSchema(fullSchema, step3Keys) : {}),
    [currentStep, fullSchema]
  )
  const step1FormContext = useMemo(
    () => ({
      contractId: formData?.contractId
    }),
    [formData?.contractId]
  )

  const resetAll = () => {
    setFormData(undefined)
    setMatrixAmountDraft(emptyMatrixAmountDraft)
    setSaveErrorMessage(undefined)
    setCurrentStep(1)
  }

  const handleStepToggle = (index: number) => {
    setCurrentStep((prev) => (prev === index ? undefined : index))
  }

  const updateFormData = (updatedFormData?: Partial<TaskFormData>) => {
    setSaveErrorMessage(undefined)
    if (
      updatedFormData &&
      'labId' in updatedFormData &&
      updatedFormData.labId !== formData?.labId
    ) {
      initializedAddressLab.current = undefined
    }
    setFormData((previousFormData) => ({
      ...previousFormData,
      ...updatedFormData,
      ...(updatedFormData &&
      'labId' in updatedFormData &&
      updatedFormData.labId !== previousFormData?.labId
        ? { labReceptionAddressId: null }
        : {})
    }))
  }

  const submitFormData = async (fd: TaskFormData): Promise<void> => {
    if (!receptionAddress.canSave) return
    try {
      setSaveErrorMessage(undefined)
      const analysisType = await existingOrCreateAnalysisType.mutateAsync({
        selectedProductId: fd.productId,
        selectedMatrixId: fd.matrixId,
        selectedSubstanceIds: fd.substanceIds,
        selectedPayloadSchemaId: fd.payloadSchemaId
      })

      // Add analysis type to contract if not already added
      const contract = contracts?.find((c) => c.id === fd.contractId)
      if (
        contract &&
        !contract.analysisTypes?.some(
          (a) => a.analysisType?.id === analysisType.id
        )
      ) {
        await addContractAnalysisType
          .mutateAsync({
            contractId: contract.id,
            body: { analysisTypeId: analysisType.id }
          })
          .then(() =>
            Mtds.toast.success('Analysetypen ble lagt til i kontrakten.')
          )
      }

      const taskNew: CreateTask = {
        planIds: [planId],
        analysisTypeId: analysisType.id,
        quantity: fd.quantity,
        sampntTypeId: fd.sampntTypeId,
        labId: fd.labId,
        labReceptionAddressId: formData?.labReceptionAddressId,
        contractId: fd.contractId,
        description: fd.description,
        ownerId: 'self',
        countSubSamples: fd.countSubSamples,
        ordningsverdiId: fd.ordningsverdiId,
        planStatus,
        subPurposeId: fd.subPurposeId,
        locationRequired: fd.locationRequired,
        matrixAmount
      }
      await createTask.mutateAsync(taskNew)
      Mtds.toast.success('Oppgaven er opprettet og lagt til i planen')
      resetAll()
      setOpen(false)
    } catch (e) {
      console.error(e)
      setSaveErrorMessage(toUserMessage(e))
    }
  }

  const handleSelectAnalysisType = (
    selectedAnalysisTypeId: number,
    selectedContractId: number
  ) => {
    const selectedContract = validAnalysisTypes.find(
      (v) => v.id === Number(selectedContractId)
    )
    const analysisType: ContractAnalysisType | undefined =
      selectedContract?.analysisTypes?.find(
        (a) => a.id === selectedAnalysisTypeId
      )
    const sampleTypeId = analysisType?.analysisType?.sampleType?.id
    const substanceIds = analysisType?.analysisType?.substances?.map(
      (s) => s.substance.id
    )
    const payloadSchemaId = analysisType?.analysisType?.payloadSchema?.id
    const labId = selectedContract?.labId
    if (labId !== formData?.labId) initializedAddressLab.current = undefined
    setFormData((prev) => {
      const base = prev ?? ({} as TaskFormData)
      if (sampleTypeId && substanceIds && labId && payloadSchemaId) {
        return {
          ...base,
          sampleTypeId,
          substanceIds,
          labId,
          payloadSchemaId,
          labReceptionAddressId:
            labId === base.labId ? base.labReceptionAddressId : null
        }
      }
      return prev
    })
  }

  const step0Activate = true
  const labName = batchData?.labs?.find(
    (lab) => lab.id === formData?.labId
  )?.name
  const step1Activate = canProceedFromStep0()
  const step2Activate = canProceedFromStep1() && hasMatrixAmount
  const step3Activate = step2Activate && canProceedFromStep2()

  const ValidAnalysisTypes = () => (
    <div>
      <Mtds.Button
        data-arrow
        data-variant='secondary'
        popoverTarget='create-task-type-popover'
      >
        Gyldige analysetyper{' '}
        <Mtds.Badge
          data-badge={validAnalysisTypes
            .map(({ analysisTypes = [] }) => analysisTypes.length)
            .reduce((a, b) => a + b, 0)}
        />
      </Mtds.Button>
      <Mtds.Popover id='create-task-type-popover' data-overscroll='contain'>
        {validAnalysisTypes?.map((contract, index) => {
          const contractTitle = contract.title
          const contractLabName = batchData?.labs.find(
            (lab) => lab.id === contract.labId
          )?.name
          return (
            <menu key={contract.id}>
              <li>
                {index > 0 && <Mtds.Divider />}
                <Mtds.Heading as={'h4'} data-size={'xs'}>
                  {contractTitle} ({contractLabName})
                </Mtds.Heading>
              </li>
              {(contract.analysisTypes ?? []).map(({ id, analysisType }) => (
                <li key={id}>
                  <Mtds.Button
                    popoverTargetAction='hide'
                    onClick={() => handleSelectAnalysisType(id, contract.id)}
                    className={'flex-row justify-between items-start'}
                  >
                    <Mtds.Grid>
                      <strong>
                        {analysisType?.sampleType?.matrix} (
                        {analysisType?.sampleType?.product})
                      </strong>
                      <Mtds.Muted>{`Skjema: ${analysisType?.payloadSchema?.name || '-'}`}</Mtds.Muted>
                    </Mtds.Grid>
                    <Mtds.Muted>{getSubstanceText(analysisType)}</Mtds.Muted>
                  </Mtds.Button>
                </li>
              ))}
            </menu>
          )
        })}
      </Mtds.Popover>
    </div>
  )
  return (
    <ModalDialog
      id='create-task-modal'
      closedby='any'
      data-placement='right'
      open={isOpen}
      onClose={() => {
        setOpen(false)
        resetAll()
      }}
    >
      <Mtds.Button
        command='close'
        commandfor={'create-task-modal'}
        aria-label={'Lukk'}
      />
      <Mtds.Grid>
        <Mtds.Heading>Opprett oppgave</Mtds.Heading>
        {/* Step 0 */}
        <Mtds.Details data-variant='card' open={currentStep === 0}>
          <StepHeader
            index={0}
            title='Laboratorium'
            currentStep={currentStep}
            canActivate={step0Activate}
            isFinished={step1Activate && receptionAddress.canSave}
            onToggleRequest={() => handleStepToggle(0)}
          />
          <Mtds.Grid className={'p-1'} data-gap={6}>
            {isPendingBatchData && <Mtds.Skeleton />}
            {isErrorBatchData && <ErrorMessage error={batchDataError} />}
            {parentContractId ? (
              <Mtds.Grid data-gap='4'>
                {[
                  { label: 'Laboratorium', value: labName },
                  { label: 'Kontrakt', value: parentContract?.title }
                ].map(({ label, value }) => (
                  <Mtds.Info
                    key={label}
                    className='grid grid-cols-[minmax(0,7.5rem)_minmax(0,1fr)] items-center gap-[var(--mtds-4)]'
                  >
                    <strong className='min-w-0 font-normal'>{label}</strong>
                    <span className='min-w-0'>{value ?? '-'}</span>
                  </Mtds.Info>
                ))}
              </Mtds.Grid>
            ) : (
              <>
                {debug && <ValidAnalysisTypes />}
                {!isPendingBatchData && !isErrorBatchData && (
                  <Form
                    noHtml5Validate
                    schema={step0Schema}
                    uiSchema={createTaskUiSchema}
                    widgets={widgets}
                    templates={templates}
                    showErrorList={false}
                    validator={validator}
                    formData={formData}
                    experimental_defaultFormStateBehavior={
                      defaultFormStateBehavior
                    }
                    onChange={(data) => updateFormData(data.formData)}
                    onSubmit={() => setCurrentStep(1)}
                  >
                    <></>
                  </Form>
                )}
              </>
            )}
            {isOpen && formData?.labId != null && (
              <TaskReceptionAddress
                query={receptionAddress}
                value={formData.labReceptionAddressId}
                disabled={isSaving}
                onChange={(labReceptionAddressId) =>
                  setFormData((current) => ({
                    ...current,
                    labReceptionAddressId
                  }))
                }
              />
            )}
          </Mtds.Grid>
        </Mtds.Details>
        {/* Step 1 */}
        <Mtds.Details data-variant='card' open={currentStep === 1}>
          <StepHeader
            index={1}
            title='Prøvemateriale'
            currentStep={currentStep}
            canActivate={step1Activate}
            alertLabel={currentStep === 1 ? undefined : step1AlertLabel}
            isFinished={step2Activate}
            onToggleRequest={() => handleStepToggle(1)}
          />
          <Mtds.Grid className={'p-1'} data-gap={6}>
            {isPendingBatchData && <Mtds.Skeleton />}
            {isErrorBatchData && <ErrorMessage error={batchDataError} />}
            {!step1Activate && (
              <Mtds.Alert data-color={'warning'}>
                Fullfør forrige steg for å fortsette
              </Mtds.Alert>
            )}
            {debug && <ValidAnalysisTypes />}
            {!isPendingBatchData && !isErrorBatchData && step1Activate && (
              <Form
                noHtml5Validate
                schema={step1Schema}
                uiSchema={createTaskUiSchema}
                widgets={widgets}
                templates={templates}
                showErrorList={false}
                validator={validator}
                formData={formData}
                formContext={step1FormContext}
                experimental_defaultFormStateBehavior={defaultFormStateBehavior}
                onChange={(data) => updateFormData(data.formData)}
                onSubmit={() => setCurrentStep(2)}
              >
                <></>
              </Form>
            )}
            {!isPendingBatchData && !isErrorBatchData && step1Activate && (
              <MatrixAmountFields
                required
                draft={matrixAmountDraft}
                error={matrixAmountError}
                disabled={isSaving}
                onChange={setMatrixAmountDraft}
              />
            )}
            {!isPendingBatchData &&
              !isErrorBatchData &&
              showAwaitingApprovalWarning && (
                <Mtds.Alert data-color={'info'}>
                  <Mtds.Heading>Avventer</Mtds.Heading>
                  Kombinasjonen avventer godkjenning hos laboratoriet.
                </Mtds.Alert>
              )}
            {!isPendingBatchData &&
              !isErrorBatchData &&
              showNewCombinationWarning && (
                <Mtds.Alert data-color={'info'}>
                  <Mtds.Heading>
                    Ny kombinasjon sendes til godkjenning
                  </Mtds.Heading>
                  Kombinasjonen sendes til godkjenning hos laboratoriet.
                </Mtds.Alert>
              )}
          </Mtds.Grid>
        </Mtds.Details>

        {/* Step 2 */}
        <Mtds.Details data-variant='card' open={currentStep === 2}>
          <StepHeader
            index={2}
            title='Tilleggsinformasjon'
            currentStep={currentStep}
            canActivate={step2Activate}
            isFinished={step3Activate}
            onToggleRequest={() => handleStepToggle(2)}
          />
          <Mtds.Grid className={'p-1'}>
            {isPendingBatchData && <Mtds.Skeleton />}
            {isErrorBatchData && <ErrorMessage error={batchDataError} />}
            {!step2Activate && (
              <Mtds.Alert data-color={'warning'}>
                Fullfør forrige steg for å fortsette
              </Mtds.Alert>
            )}
            {!isPendingBatchData && !isErrorBatchData && step2Activate && (
              <Form
                noHtml5Validate
                schema={step2Schema}
                uiSchema={createTaskUiSchema}
                widgets={widgets}
                templates={templates}
                showErrorList={false}
                validator={validator}
                formData={formData}
                experimental_defaultFormStateBehavior={defaultFormStateBehavior}
                onChange={(data) => updateFormData(data.formData)}
                onSubmit={() => setCurrentStep(3)}
              >
                <></>
              </Form>
            )}
          </Mtds.Grid>
        </Mtds.Details>

        {/* Step 3 */}
        <Mtds.Details data-variant='card' open={currentStep === 3}>
          <StepHeader
            index={3}
            title='Antall prøver'
            currentStep={currentStep}
            canActivate={step3Activate}
            isFinished={canSubmit()}
            onToggleRequest={() => handleStepToggle(3)}
          />
          <Mtds.Grid className={'p-1'}>
            {isPendingBatchData && <Mtds.Skeleton />}
            {isErrorBatchData && <ErrorMessage error={batchDataError} />}
            {!step3Activate && (
              <Mtds.Alert data-color={'warning'}>
                Fullfør forrige steg for å fortsette
              </Mtds.Alert>
            )}
            {!isPendingBatchData && !isErrorBatchData && step3Activate && (
              <Form
                noHtml5Validate
                schema={step3Schema}
                uiSchema={createTaskUiSchema}
                widgets={widgets}
                templates={templates}
                showErrorList={false}
                validator={validator}
                formData={formData}
                experimental_defaultFormStateBehavior={defaultFormStateBehavior}
                onChange={(data) => updateFormData(data.formData)}
                onSubmit={({ formData }) => {
                  if (canSubmit() && formData) {
                    submitFormData(formData as TaskFormData)
                  }
                }}
              >
                {saveErrorMessage && (
                  <Mtds.Validation className='mt-4 whitespace-normal break-words'>
                    Kunne ikke opprette oppgaven: {saveErrorMessage}
                  </Mtds.Validation>
                )}
                <Mtds.Flex className='mt-8'>
                  <Mtds.Button
                    data-variant='primary'
                    type='submit'
                    aria-busy={isSaving}
                    disabled={!canSubmit() || isSaving}
                  >
                    Opprett
                  </Mtds.Button>
                </Mtds.Flex>
              </Form>
            )}
          </Mtds.Grid>
        </Mtds.Details>
      </Mtds.Grid>
    </ModalDialog>
  )
}
